import { useCallback, useEffect, useMemo, useState } from "react";

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

const IOS_SAFARI = /iphone|ipad|ipod/i.test(navigator.userAgent ?? "");

export interface InstallPrompt {
  /** True on Chromium when installable (deferred prompt captured, not standalone). */
  canInstall: boolean;
  /** True when running as an installed PWA. */
  isStandalone: boolean;
  /** True on iOS Safari where beforeinstallprompt doesn't exist. */
  needsIosGuidance: boolean;
  /** Show the deferred Chromium prompt. */
  promptInstall: () => Promise<"accepted" | "dismissed" | "unavailable">;
}

function isStandaloneDisplay(): boolean {
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ??
    (navigator as unknown as { standalone?: boolean }).standalone === true
  );
}

/**
 * PWA installability (technical-spec §6.4): capture + defer beforeinstallprompt
 * on Chromium, auto-hide once installed, and surface iOS Add-to-Home-Screen
 * guidance where the event doesn't exist.
 */
export function useInstallPrompt(): InstallPrompt {
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null);
  const [installed, setInstalled] = useState(isStandaloneDisplay());
  // Display mode is read once at mount: it cannot change without a reload,
  // so there is no setter and nothing to subscribe to.
  const [standalone] = useState(isStandaloneDisplay());

  useEffect(() => {
    const onPrompt = (event: Event) => {
      event.preventDefault();
      setDeferred(event as BeforeInstallPromptEvent);
    };
    const onInstalled = () => {
      setInstalled(true);
      setDeferred(null);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const promptInstall = useCallback(async (): Promise<"accepted" | "dismissed" | "unavailable"> => {
    if (!deferred) return "unavailable";
    await deferred.prompt();
    const choice = await deferred.userChoice;
    if (choice.outcome === "accepted") {
      setInstalled(true);
      setDeferred(null);
    }
    return choice.outcome;
  }, [deferred]);

  return useMemo(
    () => ({
      canInstall: Boolean(deferred) && !installed && !standalone,
      isStandalone: standalone,
      needsIosGuidance: IOS_SAFARI && !installed && !standalone,
      promptInstall,
    }),
    [deferred, installed, standalone, promptInstall]
  );
}
